"use client";

import { useIsMobile } from "@/modules/hooks/devices/useDevice";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  Cancel01Icon,
} from "hugeicons-react";
import Image from "next/image";
import { useEffect, useState } from "react";

const ListImagesPopupView = ({ images }: { images: string[] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [maxDisplayImages, setMaxDisplayImages] = useState(4);
  const isMobile = useIsMobile();

  const displayImages = images.slice(0, maxDisplayImages);
  const remainingImages = images.length - maxDisplayImages;

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === "ArrowRight") {
        handleNextImage();
      } else if (e.key === "ArrowLeft") {
        handlePreviousImage();
      } else if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (isMobile) {
      setMaxDisplayImages(2);
    } else {
      setMaxDisplayImages(4);
    }
  }, [isMobile]);

  if (images.length === 0) return null;

  return (
    <>
      {/* Image List Display */}
      <div
        className="flex gap-2 cursor-pointer w-full"
        onClick={() => setIsOpen(true)}
      >
        {displayImages.map((image, index) => (
          <div
            key={index}
            className={`relative ${isMobile ? "w-1/2" : "w-1/3"}`}
          >
            <Image
              src={image}
              alt={`Image ${index + 1}`}
              width={700}
              height={530}
              className="w-full h-full object-cover rounded-lg"
            />
            {index === maxDisplayImages - 1 && remainingImages > 0 && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                <span className="text-white text-lg font-bold">
                  +{remainingImages}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Image Dialog */}
      <Dialog
        transition
        open={isOpen}
        onClose={() => setIsOpen(false)}
        className="fixed z-[100] inset-0 flex w-screen items-center justify-center bg-black/30 p-4"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-black/30 duration-300 ease-out data-[closed]:opacity-0"
        />
        <DialogPanel
          transition
          className="relative bg-white rounded-lg p-4 max-w-3xl w-full duration-300 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
        >
          <div className="flex justify-between items-center mb-4">
            <DialogTitle className="text-xl font-bold text-gray-700">
              Image Gallery ({currentImageIndex + 1}/{images.length})
            </DialogTitle>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <Cancel01Icon />
            </button>
          </div>

          <div className="relative">
            <Image
              src={images[currentImageIndex]}
              alt={`Image ${currentImageIndex + 1}`}
              width={1200}
              height={900}
              className="w-full h-[500px] object-contain"
            />

            {/* Navigation Buttons */}
            <button
              onClick={handlePreviousImage}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 p-2 rounded-full"
            >
              <ArrowLeft01Icon className="text-gray-700" />
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 p-2 rounded-full"
            >
              <ArrowRight01Icon className="text-gray-700" />
            </button>
          </div>

          {/* Thumbnail Navigation */}
          <div className="flex gap-2 mt-4 overflow-x-auto p-2">
            {images.map((image, index) => (
              <Image
                key={index}
                src={image}
                alt={`Thumbnail ${index + 1}`}
                width={300}
                height={230}
                className={`w-16 h-16 object-cover cursor-pointer rounded-lg
                  ${
                    currentImageIndex === index ? "border-2 border-primary" : ""
                  }`}
                onClick={() => setCurrentImageIndex(index)}
              />
            ))}
          </div>
        </DialogPanel>
      </Dialog>
    </>
  );
};

ListImagesPopupView.displayName = "ListImagesPopupView";
export default ListImagesPopupView;
